import React, { useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { AnimatedChip, ChipColor, GenericButton, Tooltip } from '@app/core';
import { ParticipationStatus } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    ...theme.fonts.h5,
    height: theme.height.regular,
    paddingRight: theme.marginBase * 2,
    paddingLeft: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    color: theme.colors.inkSecondary,
    borderRadius: theme.borderRadius.std,
    transition:
      'background-color 0.3s ease-in-out, box-shadow 0.3s ease-in-out',

    '&:hover, &:active, &:focus': {
      backgroundColor: theme.colors.white,
    },
  },
  selected: {
    backgroundColor: theme.colors.white,
    boxShadow: theme.boxShadow.base,
  },
}));

interface Props<T extends string> {
  name: T;
  label: string;
  statusList: {
    color: ChipColor;
    statuses: ParticipationStatus[];
    label?: string;
  }[];
  countByStatus?: Record<ParticipationStatus, number>;
  visibleOnDataOnly?: boolean;
  onClick?: (selected: T) => any;
  selected?: boolean;
}

export const MergedStatusTab = <T extends string>({
  label,
  statusList,
  visibleOnDataOnly,
  name,
  onClick,
  selected,
  countByStatus,
}: Props<T>) => {
  const classes = useStyles();

  const getCountForStatuses = (statuses: ParticipationStatus[]) =>
    statuses.reduce((sum, status) => {
      if (!countByStatus) {
        return undefined;
      }
      const baseCount = countByStatus?.[status] ?? 0;
      return sum ? sum + baseCount : baseCount;
    }, undefined as undefined | number);

  const totalCount = statusList.reduce(
    (acc, s) => acc + (getCountForStatuses(s.statuses) || 0),
    0,
  );

  const _onClick = useCallback(() => {
    onClick && onClick(name);
  }, [name, onClick]);

  if (visibleOnDataOnly && !totalCount) {
    return null;
  }

  return (
    <GenericButton
      onClick={_onClick}
      className={classnames(classes.button, {
        [classes.selected]: selected,
      })}
    >
      {label}
      {statusList.map((s, index) => {
        const tooltipText = statusList.length === 1 ? label : s.label || '';
        const chipCount = getCountForStatuses(s.statuses);
        if (chipCount === undefined) {
          return null;
        }

        return (
          <Tooltip
            key={index}
            title={tooltipText}
            placement="top"
            arrow
            requireDiv
          >
            <AnimatedChip value={chipCount} color={s.color} />
          </Tooltip>
        );
      })}
    </GenericButton>
  );
};
